import {
  forwardRef,
  type PropsWithoutRef,
  type ReactElement,
  type Ref,
  type RefAttributes,
} from 'react';

import { BaseInput, type BaseInputProps } from './components/BaseInput';
import {
  CurrencyInput,
  type CurrencyInputProps,
} from './components/CurrencyInput';
import { TextArea, type TextAreaProps } from './components/TextArea';

export type { BaseInputProps, CurrencyInputProps, TextAreaProps };

export type InputTextProps =
  | (PropsWithoutRef<BaseInputProps> & RefAttributes<HTMLInputElement>)
  | (PropsWithoutRef<Omit<TextAreaProps, 'type'> & { type: 'textarea' }> &
      RefAttributes<HTMLTextAreaElement>);

const InputRoot = forwardRef<HTMLInputElement, BaseInputProps>((props, ref) => (
  <BaseInput ref={ref} {...props} />
));

InputRoot.displayName = 'Input';

const InputTextWithRef = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  InputTextProps
>((props, ref) => {
  if (props.type === 'textarea') {
    const { type: _type, ...textAreaProps } = props;

    return (
      <TextArea ref={ref as Ref<HTMLTextAreaElement>} {...textAreaProps} />
    );
  }

  return <BaseInput ref={ref as Ref<HTMLInputElement>} {...props} />;
});

InputTextWithRef.displayName = 'Input.Text';

const InputText = InputTextWithRef as (
  props: InputTextProps,
) => ReactElement | null;

const InputCurrency = forwardRef<HTMLInputElement, CurrencyInputProps>(
  (props, ref) => <CurrencyInput ref={ref} {...props} />,
);

InputCurrency.displayName = 'Input.Currency';

export const Input = Object.assign(InputRoot, {
  Text: InputText,
  Currency: InputCurrency,
});
